import React, { useState, useEffect, useCallback } from 'react';
import { getDomains, deleteDomain, createDomain, updateDomain } from '../api/domainapi';
import DomainModal from './DomainModal';
import Layout from './container/Layout';

const DomainList = () => {
    // States for domain list
    const [domains, setDomains] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // States for modal/popup
    const [showModal, setShowModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentDomain, setCurrentDomain] = useState({
        domain: '',
        ipAddresses: [],
        emails: []
    });

    // State for IP address and email management
    const [currentIP, setCurrentIP] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');

    // Fetch domains on component mount
    const fetchDomains = useCallback(async () => {
        try {
            setLoading(true);
            const data = await getDomains();
            setDomains(data);
            setError('');
        } catch (err) {
            setError('Failed to fetch domains');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDomains();
    }, [fetchDomains]);

    // Handle domain deletion
    const handleDelete = useCallback(async (id) => {
        if (window.confirm('Are you sure you want to delete this domain?')) {
            try {
                await deleteDomain(id);
                setDomains(prevDomains => prevDomains.filter(domain => domain._id !== id));
            } catch (err) {
                setError('Failed to delete domain');
                console.error(err);
            }
        }
    }, []);

    // Open modal for adding new domain
    const handleAddDomain = useCallback(() => {
        setIsEditing(false);
        setCurrentDomain({
            domain: '',
            ipAddresses: [],
            emails: []
        });
        setCurrentIP('');
        setCurrentEmail('');
        setShowModal(true);
    }, []);

    // Open modal for editing existing domain
    const handleEditDomain = useCallback((domain) => {
        setIsEditing(true);
        setCurrentDomain({
            ...domain,
            ipAddresses: [...domain.ipAddresses],
            emails: domain.emails ? [...domain.emails] : []
        });
        setCurrentIP('');
        setCurrentEmail('');
        setShowModal(true);
    }, []);

    // Handle input changes for domain fields
    const handleDomainChange = useCallback((e) => {
        const { name, value } = e.target;
        setCurrentDomain(prevDomain => ({
            ...prevDomain,
            [name]: value
        }));
    }, []);

    // Handle IP address input changes
    const handleIPChange = useCallback((e) => {
        setCurrentIP(e.target.value);
    }, []);

    // Handle email input changes
    const handleEmailChange = useCallback((e) => {
        setCurrentEmail(e.target.value);
    }, []);

    // Add IP address to current domain
    const handleAddIP = useCallback(() => {
        // Basic IP validation (simple regex for IPv4)
        const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!currentIP) {
            alert('Please enter an IP address');
            return;
        }
        if (!ipv4Regex.test(currentIP)) {
            alert('Please enter a valid IPv4 address');
            return;
        }

        // Check if IP already exists
        if (currentDomain.ipAddresses.includes(currentIP)) {
            alert('This IP address is already added');
            return;
        }

        // Add IP to current domain
        setCurrentDomain(prevDomain => ({
            ...prevDomain,
            ipAddresses: [...prevDomain.ipAddresses, currentIP]
        }));

        // Reset current IP
        setCurrentIP('');
    }, [currentIP, currentDomain.ipAddresses]);

    // Add email to current domain
    const handleAddEmail = useCallback(() => {
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!currentEmail) {
            alert('Please enter an email address');
            return;
        }
        if (!emailRegex.test(currentEmail)) {
            alert('Please enter a valid email address');
            return;
        }

        // Check if email already exists
        if (currentDomain.emails.includes(currentEmail)) {
            alert('This email is already added');
            return;
        }

        // Add email to current domain
        setCurrentDomain(prevDomain => ({
            ...prevDomain,
            emails: [...prevDomain.emails, currentEmail]
        }));

        // Reset current email
        setCurrentEmail('');
    }, [currentEmail, currentDomain.emails]);

    // Remove IP from current domain
    const handleRemoveIP = useCallback((index) => {
        setCurrentDomain(prevDomain => {
            const updatedIPs = [...prevDomain.ipAddresses];
            updatedIPs.splice(index, 1);
            return {
                ...prevDomain,
                ipAddresses: updatedIPs
            };
        });
    }, []);

    // Remove email from current domain
    const handleRemoveEmail = useCallback((index) => {
        setCurrentDomain(prevDomain => {
            const updatedEmails = [...prevDomain.emails];
            updatedEmails.splice(index, 1);
            return {
                ...prevDomain,
                emails: updatedEmails
            };
        });
    }, []);

    // Handle form submission
    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        // Validate form
        if (!currentDomain.domain) {
            alert('Domain name is required');
            return;
        }

        if (currentDomain.ipAddresses.length === 0) {
            alert('At least one IP address is required');
            return;
        }

        try {
            const domainData = {
                ...currentDomain,
                createdAt: isEditing ? currentDomain.createdAt : Date.now(),
                updatedAt: Date.now()
            };

            if (isEditing) {
                const updatedDomain = await updateDomain(currentDomain._id, domainData);
                setDomains(prevDomains => prevDomains.map(domain => domain._id === updatedDomain._id ? updatedDomain : domain));
            } else {
                const newDomain = await createDomain(domainData);
                setDomains(prevDomains => [...prevDomains, newDomain]);
            }

            setShowModal(false);
        } catch (err) {
            setError(`Failed to ${isEditing ? 'update' : 'create'} domain`);
            console.error(err);
        }
    }, [currentDomain, isEditing]);

    // Content rendering based on loading/error states
    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center my-12">
                    <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                </div>
            );
        }

        if (error) {
            return (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <>
                {domains.length === 0 ? (
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        <div className="p-8 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No domains found</h3>
                            <p className="mt-1 text-sm text-gray-500">Get started by adding a new domain.</p>
                            <div className="mt-6">
                                <button
                                    onClick={handleAddDomain}
                                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                                    </svg>
                                    Add Domain
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="overflow-x-auto shadow rounded-lg">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Domain
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        IP Addresses
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email Addresses
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Created At
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Updated At
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {domains.map(domain => (
                                    <tr key={domain._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{domain.domain}</div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {domain.ipAddresses.map((ip, idx) => (
                                                    <span key={idx} className="px-2 py-1 text-xs bg-gray-100 rounded text-gray-800">
                                                        {ip}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-wrap gap-1">
                                                {domain.emails && domain.emails.map((email, idx) => (
                                                    <span key={idx} className="px-2 py-1 text-xs bg-blue-100 rounded text-blue-800">
                                                        {email}
                                                    </span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(domain.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(domain.updatedAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEditDomain(domain)}
                                                className="text-blue-600 hover:text-blue-900 mr-4"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                onClick={() => handleDelete(domain._id)}
                                                className="text-red-600 hover:text-red-900"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    };

    return (
        <Layout>
            <div className="sm:flex sm:items-center sm:justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Domains</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Manage your domain names and associated IPs and emails
                    </p>
                </div>
                <button
                    onClick={handleAddDomain}
                    className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                    <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Add Domain
                </button>
            </div>

            {renderContent()}

            {showModal && (
                <DomainModal
                    isEditing={isEditing}
                    currentDomain={currentDomain}
                    handleDomainChange={handleDomainChange}
                    handleSubmit={handleSubmit}
                    handleIPChange={handleIPChange}
                    currentIP={currentIP}
                    handleAddIP={handleAddIP}
                    handleRemoveIP={handleRemoveIP}
                    handleEmailChange={handleEmailChange}
                    currentEmail={currentEmail}
                    handleAddEmail={handleAddEmail}
                    handleRemoveEmail={handleRemoveEmail}
                    setShowModal={setShowModal}
                />
            )}
        </Layout>
    );
};

export default DomainList;